import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jobsAPI, applicationsAPI, documentsAPI, userAPI } from '../api/api';
import { setPageMeta } from '../utils/seo';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import SaveJobButton from '../components/SaveJobButton';
import { WeatherStrip } from '../components/WeatherPanel';
import MatchingWorkersModal from '../components/MatchingWorkersModal';
import JobPhotoGallery from '../components/JobPhotoGallery';
import JobCostTracker from '../components/JobCostTracker';
import TimeClockWidget from '../components/TimeClockWidget';
import JobDocuments from '../components/JobDocuments';
import ClientInviteManager from '../components/ClientInviteManager';
import CreateDocumentModal from '../components/CreateDocumentModal';
import { UpgradePrompt } from '../components/SubscriptionUI';
import { useSubscription } from '../contexts/SubscriptionContext';
import { 
  MapPin, 
  Clock, 
  ArrowLeft,
  CurrencyDollar,
  Briefcase,
  CheckCircle,
  PaperPlaneTilt,
  Warning,
  SpinnerGap,
  Buildings,
  CalendarBlank,
  Users,
  Sparkle,
  FileText,
  Receipt
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const JobDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [parentJob, setParentJob] = useState(null);

  useEffect(() => {
    const pj = job?.parent_job_id;
    if (!pj) { setParentJob(null); return; }
    jobsAPI.getById(pj)
      .then((r) => setParentJob(r.data.job || r.data))
      .catch(() => setParentJob(null));
  }, [job?.parent_job_id]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showApplyForm, setShowApplyForm] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [isWorker, setIsWorker] = useState(false);
  const [isContractor, setIsContractor] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [isAssignedWorker, setIsAssignedWorker] = useState(false);
  const [showMatchingWorkers, setShowMatchingWorkers] = useState(false);
  const [showCreateDoc, setShowCreateDoc] = useState(false);
  const [createDocType, setCreateDocType] = useState('contract'); // 'contract' | 'change_order'
  const [docPrefill, setDocPrefill] = useState({ signers: [], parentDocumentId: null });
  const [jobDocs, setJobDocs] = useState([]);
  const [assignedWorkerInfo, setAssignedWorkerInfo] = useState(null);
  const [application, setApplication] = useState({
    coverLetter: '',
    proposedRate: ''
  });
  const [errors, setErrors] = useState({});
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const { refresh: refreshSubscription } = useSubscription();

  useEffect(() => {
    const userType = localStorage.getItem('userType');
    setIsWorker(userType === 'worker');
    setIsContractor(userType === 'contractor');
  }, []);

  const fetchJobDetails = React.useCallback(async () => {
    try {
      setLoading(true);
      const response = await jobsAPI.getById(jobId);
      setJob(response.data.job);

      // SEO: per-job meta tags + JSON-LD JobPosting
      const j = response.data.job;
      if (j) {
        const budgetStr = j.budget_min && j.budget_max
          ? `$${Number(j.budget_min).toLocaleString()}–$${Number(j.budget_max).toLocaleString()}`
          : '';
        const bits = [budgetStr, j.duration, (j.required_skills || []).slice(0, 4).join(', ')].filter(Boolean);
        setPageMeta({
          title: `${j.title}${j.location ? ' — ' + j.location : ''} | ConstructHelp Jobs`,
          description: `${(j.description || '').slice(0, 150)}${bits.length ? ' · ' + bits.join(' · ') + '.' : ''} Apply free on ConstructHelp.`,
          url: `${window.location.origin}/api/jobs/og/${jobId}`,
          image: `${window.location.origin}/og-default.png`,
          jsonLd: {
            '@context': 'https://schema.org/',
            '@type': 'JobPosting',
            title: j.title,
            description: j.description || j.title,
            datePosted: (j.created_at || '').slice(0, 10),
            employmentType: 'CONTRACTOR',
            directApply: true,
            hiringOrganization: { '@type': 'Organization', name: j.company || 'ConstructHelp Contractor' },
            jobLocation: { '@type': 'Place', address: { '@type': 'PostalAddress', addressLocality: j.location || '', addressCountry: 'US' } },
            url: `${window.location.origin}/job/${jobId}`,
          },
        });
      }
      
      // Check if current user is the owner
      const userId = localStorage.getItem('userId');
      const userType = localStorage.getItem('userType');
      const iAmOwner = response.data.job?.contractor_id === userId;
      if (iAmOwner) setIsOwner(true);

      // For contractor owners: fetch job documents + accepted application's worker for prefill
      if (iAmOwner && userType === 'contractor') {
        try {
          const [docsRes, appsRes] = await Promise.all([
            documentsAPI.list({ job_id: jobId }),
            applicationsAPI.getJobApplications(jobId),
          ]);
          setJobDocs(docsRes.data.documents || []);
          const apps = appsRes.data.applications || [];
          const accepted = apps.find(a => a.status === 'accepted' && a.worker);
          if (accepted?.worker) {
            setAssignedWorkerInfo({
              user_id: accepted.worker._id,
              role: 'worker',
              name: accepted.worker.name || '',
              email: accepted.worker.email || '',
            });
          }
        } catch (err) {
          console.error('Error loading contract context:', err);
        }
      }
      
      // Check if worker has already applied
      const token = localStorage.getItem('token');
      if (token && (userType === 'worker' || userType === 'contractor')) {
        try {
          const appsResponse = await applicationsAPI.getMyApplications();
          const myApps = appsResponse.data.applications || [];
          const alreadyApplied = myApps.some(app => app.job_id === jobId);
          setHasApplied(alreadyApplied);
          // Check if worker is assigned (accepted application)
          const acceptedApp = myApps.find(app => app.job_id === jobId && app.status === 'accepted');
          setIsAssignedWorker(!!acceptedApp);
        } catch (err) {
          console.error('Error checking applications:', err);
        }
      }
    } catch (error) {
      console.error('Error fetching job details:', error);
      toast({
        title: 'Error',
        description: 'Failed to load job details',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchJobDetails();
  }, [fetchJobDetails]);

  const validateForm = () => {
    const newErrors = {};
    if (!application.coverLetter.trim()) {
      newErrors.coverLetter = 'Please write a cover letter';
    } else if (application.coverLetter.trim().length < 50) {
      newErrors.coverLetter = 'Cover letter must be at least 50 characters';
    }
    if (!application.proposedRate.trim()) {
      newErrors.proposedRate = 'Please enter your proposed rate';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleApply = async (e) => {
    e.preventDefault();
    
    // Check if user is logged in
    const token = localStorage.getItem('token');
    const userType = localStorage.getItem('userType');
    
    if (!token) {
      toast({
        title: 'Login Required',
        description: 'Please login as a worker to apply for jobs',
        variant: 'destructive'
      });
      navigate('/login?type=worker');
      return;
    }

    const allowedType = (job?.open_to === 'subcontractors') ? ['contractor']
      : (job?.open_to === 'both') ? ['worker', 'contractor'] : ['worker'];
    if (!allowedType.includes(userType)) {
      toast({
        title: job?.open_to === 'subcontractors' ? 'Subcontractors Only' : 'Workers Only',
        description: job?.open_to === 'subcontractors'
          ? 'This job is open to licensed contractors only.'
          : 'Only workers can apply for this job. Please login with a worker account.',
        variant: 'destructive'
      });
      return;
    }

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      await applicationsAPI.create({
        job_id: jobId,
        cover_letter: application.coverLetter.trim(),
        proposed_rate: application.proposedRate.trim()
      });

      toast({
        title: 'Application Submitted!',
        description: 'Your application has been sent to the contractor. Good luck!',
      });
      
      setHasApplied(true);
      setShowApplyForm(false);
      setApplication({ coverLetter: '', proposedRate: '' });
      
      // Refresh job data to update application count
      fetchJobDetails();
      refreshSubscription();
    } catch (error) {
      console.error('Error submitting application:', error);
      const errorDetail = error.response?.data?.detail;
      
      // Check for plan limit error (402 from new gates, 403 with upgrade_required from legacy gates)
      if (error.response?.status === 402 || (error.response?.status === 403 && errorDetail?.upgrade_required)) {
        setUpgradeInfo(errorDetail);
      } else {
        toast({
          title: 'Error',
          description: typeof errorDetail === 'string' ? errorDetail : (errorDetail?.message || 'Failed to submit application. Please try again.'),
          variant: 'destructive'
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveJob = () => {
    toast({
      title: 'Job Saved',
      description: 'This job has been added to your saved jobs.',
    });
  };

  const handleStartApplication = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast({
        title: 'Login Required',
        description: 'Please login as a worker to apply for jobs',
        variant: 'destructive'
      });
      navigate('/login?type=worker');
      return;
    }
    
    const userType = localStorage.getItem('userType');
    if (userType !== 'worker') {
      toast({
        title: 'Workers Only',
        description: 'Only workers can apply for jobs.',
        variant: 'destructive'
      });
      return;
    }
    
    setShowApplyForm(true);
  };

  const launchContractDraft = async () => {
    // Pull contractor's identity from /auth/me for accurate signer record
    let contractorSigner = null;
    try {
      const meRes = await userAPI.getMe();
      const me = meRes.data?.user || meRes.data;
      contractorSigner = {
        user_id: me._id,
        role: 'contractor',
        name: me.name || '',
        email: me.email || '',
      };
    } catch (err) {
      console.error('Could not load self for contract:', err);
    }

    const signers = [];
    if (contractorSigner) signers.push(contractorSigner);
    if (assignedWorkerInfo) signers.push(assignedWorkerInfo);

    setCreateDocType('contract');
    setDocPrefill({ signers, parentDocumentId: null });
    setShowCreateDoc(true);
  };

  const launchChangeOrderDraft = async () => {
    // Find a signed parent contract for this job
    const parent = jobDocs.find(d => d.document_type === 'contract' && d.status === 'signed');
    if (!parent) {
      toast({
        title: 'No signed contract yet',
        description: 'Create and fully sign a contract before issuing a change order.',
        variant: 'destructive',
      });
      return;
    }

    let contractorSigner = null;
    try {
      const meRes = await userAPI.getMe();
      const me = meRes.data?.user || meRes.data;
      contractorSigner = {
        user_id: me._id,
        role: 'contractor',
        name: me.name || '',
        email: me.email || '',
      };
    } catch (err) {
      console.error('Could not load self:', err);
    }

    const signers = [];
    if (contractorSigner) signers.push(contractorSigner);
    if (assignedWorkerInfo) signers.push(assignedWorkerInfo);

    setCreateDocType('change_order');
    setDocPrefill({ signers, parentDocumentId: parent._id });
    setShowCreateDoc(true);
  };

  const handleDocCreated = (doc) => {
    setJobDocs(prev => [doc, ...prev]);
    navigate(`/documents/${doc._id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F0F0F2] flex items-center justify-center pb-24">
        <div className="text-center">
          <SpinnerGap size={64} weight="bold" className="animate-spin text-[#2B4C6F] mx-auto mb-4" />
          <p className="text-gray-700 text-lg font-bold">Loading job...</p>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-[#F0F0F2] flex items-center justify-center pb-24">
        <div className="text-center">
          <Warning size={64} weight="bold" className="text-gray-400 mx-auto mb-4" />
          <p className="text-gray-700 text-xl font-bold mb-4">Job not found</p>
          <button 
            onClick={() => navigate('/browse-jobs')}
            className="h-14 px-6 bg-[#2B4C6F] text-white font-bold text-lg rounded-xl border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
          >
            Back to Jobs
          </button>
        </div>
      </div>
    );
  }

  // Mock similar jobs - removed for cleaner UI
  const similarJobs = [];

  return (
    <div className="min-h-screen bg-[#F0F0F2] pb-24" data-testid="job-details-page">
      {/* Simplified Header with Pay */}
      <div className="bg-[#2B4C6F] text-white py-6 border-b-4 border-black">
        <div className="max-w-4xl mx-auto px-4">
          <button 
            onClick={() => navigate('/browse-jobs')}
            className="flex items-center gap-2 text-white/90 hover:text-white font-bold mb-4"
          >
            <ArrowLeft size={24} weight="bold" />
            Back to Jobs
          </button>
          
          {/* Big Pay Display */}
          <div className="flex items-center gap-3 mb-3">
            <CurrencyDollar size={40} weight="bold" className="text-white" />
            <span className="text-4xl md:text-5xl font-black">
              ${job.budget_max?.toLocaleString() || job.budget_min?.toLocaleString() || 'Negotiable'}
            </span>
          </div>
          
          <h1 className="text-2xl md:text-3xl font-bold mb-3">{job.title}</h1>
          {parentJob && (
            <button
              onClick={() => navigate(`/job/${job.parent_job_id}`)}
              className="inline-flex items-center gap-1.5 px-3 py-1 mb-3 rounded-full text-xs font-bold bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-300 border-2 border-[#EA580C] hover:bg-orange-200 transition-colors"
              data-testid="parent-job-chip"
            >
              Part of project: {parentJob.title} →
            </button>
          )}
          
          <div className="flex flex-wrap gap-4 text-lg">
            <div className="flex items-center gap-2">
              <MapPin size={24} weight="bold" />
              <span className="font-medium">{job.location}</span>
            </div>
            {job.duration && (
              <div className="flex items-center gap-2">
                <Clock size={24} weight="bold" />
                <span className="font-medium">{job.duration}</span>
              </div>
            )}
          </div>
          
          {/* Status Badge */}
          <div className="mt-4">
            <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-base font-bold border-2 ${
              job.status === 'open' 
                ? 'bg-green-500 border-green-700 text-white' 
                : 'bg-gray-200 border-gray-400 text-gray-700'
            }`}>
              {job.status === 'open' ? (
                <>
                  <CheckCircle size={20} weight="fill" />
                  Hiring Now
                </>
              ) : job.status}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Job Site Weather */}
        {job.location && (
          <div className="bg-white border-2 border-black rounded-xl p-4 shadow-[4px_4px_0px_#000] mb-6" data-testid="job-weather-card">
            <p className="text-xs font-black uppercase tracking-widest text-neutral-600 mb-2">Job Site Weather — {job.location}</p>
            <WeatherStrip location={job.location} />
          </div>
        )}

        {/* Quick Apply Section - Most Important */}
        <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000] mb-6">
          {/* Contractor: Find Matching Workers */}
          {isOwner && isContractor && (
            <button 
              onClick={() => setShowMatchingWorkers(true)}
              className="w-full h-16 flex items-center justify-center gap-3 bg-amber-500 text-white font-bold text-xl rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-1 active:shadow-none transition-all mb-4"
              data-testid="find-matching-workers-btn"
            >
              <Sparkle size={28} weight="fill" />
              Find Matching Workers
            </button>
          )}

          {/* Contractor (owner): Send Contract / Change Order */}
          {isOwner && isContractor && (
            <div className="mb-4 p-4 bg-neutral-50 border-2 border-black rounded-xl" data-testid="legal-documents-card">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-black uppercase tracking-widest text-neutral-600">
                  Legal Documents
                </p>
                {jobDocs.length > 0 && (
                  <button
                    onClick={() => navigate(`/documents?job_id=${jobId}`)}
                    className="text-xs font-bold text-[#2B4C6F] hover:underline"
                    data-testid="view-job-docs-link"
                  >
                    View all ({jobDocs.length}) →
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <button
                  onClick={launchContractDraft}
                  className="h-12 flex items-center justify-center gap-2 bg-[#2B4C6F] text-white font-bold text-sm rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                  data-testid="send-contract-btn"
                >
                  <FileText size={16} weight="bold" />
                  Send Contract
                </button>
                <button
                  onClick={launchChangeOrderDraft}
                  disabled={!jobDocs.some(d => d.document_type === 'contract' && d.status === 'signed')}
                  className="h-12 flex items-center justify-center gap-2 bg-purple-600 text-white font-bold text-sm rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
                  data-testid="send-change-order-btn"
                  title={
                    jobDocs.some(d => d.document_type === 'contract' && d.status === 'signed')
                      ? 'Send a change order for the active contract'
                      : 'Requires a signed contract first'
                  }
                >
                  <Receipt size={16} weight="bold" />
                  Send Change Order
                </button>
              </div>
              {assignedWorkerInfo ? (
                <p className="text-[10px] text-neutral-500 mt-2">
                  Signers will be pre-filled: you and <strong>{assignedWorkerInfo.name}</strong>.
                </p>
              ) : (
                <p className="text-[10px] text-amber-700 mt-2">
                  Tip: assign a worker first so they auto-populate as a signer.
                </p>
              )}
            </div>
          )}

          {/* Assigned worker: documents shortcut */}
          {isAssignedWorker && (
            <button
              onClick={() => navigate(`/documents?job_id=${jobId}`)}
              className="w-full h-12 flex items-center justify-center gap-2 bg-white text-neutral-900 font-bold text-sm rounded-lg border-2 border-black shadow-[3px_3px_0px_#000] hover:shadow-[4px_4px_0px_#000] active:translate-y-1 active:shadow-none transition-all mb-4"
              data-testid="worker-view-docs-btn"
            >
              <FileText size={16} weight="bold" />
              View Contracts & Change Orders for this Job
            </button>
          )}
          
          {/* Worker: Apply */}
          {hasApplied ? (
            <div className="bg-green-100 border-2 border-green-600 rounded-xl p-6 text-center">
              <CheckCircle size={48} weight="fill" className="text-green-600 mx-auto mb-3" />
              <p className="font-bold text-xl text-green-800">You Applied!</p>
              <p className="text-green-700 mt-1">We'll notify you when the contractor responds</p>
            </div>
          ) : !showApplyForm && ((isWorker && (job?.open_to || 'workers') !== 'subcontractors') || (isContractor && !isOwner && ['subcontractors', 'both'].includes(job?.open_to))) && (
            <div className="space-y-4">
              <button 
                onClick={handleStartApplication}
                className="w-full h-[72px] flex items-center justify-center gap-3 bg-[#2B4C6F] text-white font-bold text-xl rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
                data-testid="apply-now-btn"
              >
                <Briefcase size={28} weight="bold" />
                Apply Now
              </button>
              <div className="flex justify-center">
                <SaveJobButton 
                  jobId={jobId} 
                  showText={true}
                  size="lg"
                  className="h-14 px-6 border-2 border-black shadow-[3px_3px_0px_#000]"
                />
              </div>
            </div>
          )}
          
          {/* Not logged in */}
          {!isWorker && !isContractor && (
            <button 
              onClick={() => navigate('/login?type=worker')}
              className="w-full h-[72px] flex items-center justify-center gap-3 bg-[#2B4C6F] text-white font-bold text-xl rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-1 active:shadow-none transition-all"
              data-testid="login-to-apply-btn"
            >
              <Briefcase size={28} weight="bold" />
              Login to Apply
            </button>
          )}
        </div>

        {/* Application Form - Simplified */}
        {showApplyForm && (
          <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000] mb-6" data-testid="application-form">
            <h2 className="text-2xl font-black text-gray-900 mb-6">Apply for This Job</h2>
            <form onSubmit={handleApply} className="space-y-6">
              <div>
                <Label htmlFor="proposedRate" className="text-lg font-bold block mb-2">
                  Your Rate
                </Label>
                <input
                  id="proposedRate"
                  value={application.proposedRate}
                  onChange={(e) => {
                    setApplication({...application, proposedRate: e.target.value});
                    if (errors.proposedRate) setErrors({...errors, proposedRate: ''});
                  }}
                  placeholder="e.g., $50/hour or $5,000 total"
                  className={`w-full h-16 px-4 text-lg border-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#2B4C6F]/20 ${errors.proposedRate ? 'border-red-500' : 'border-black focus:border-[#2B4C6F]'}`}
                  data-testid="proposed-rate-input"
                />
                {errors.proposedRate && (
                  <p className="text-red-600 font-bold mt-2 flex items-center gap-2">
                    <Warning size={20} weight="fill" />
                    {errors.proposedRate}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="coverLetter" className="text-lg font-bold block mb-2">
                  Why Hire You?
                </Label>
                <Textarea
                  id="coverLetter"
                  value={application.coverLetter}
                  onChange={(e) => {
                    setApplication({...application, coverLetter: e.target.value});
                    if (errors.coverLetter) setErrors({...errors, coverLetter: ''});
                  }}
                  placeholder="Tell them about your experience..."
                  rows={5}
                  className={`w-full p-4 text-lg border-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#2B4C6F]/20 ${errors.coverLetter ? 'border-red-500' : 'border-black focus:border-[#2B4C6F]'}`}
                  data-testid="cover-letter-input"
                />
                <div className="flex justify-between mt-2">
                  {errors.coverLetter ? (
                    <p className="text-red-600 font-bold flex items-center gap-2">
                      <Warning size={20} weight="fill" />
                      {errors.coverLetter}
                    </p>
                  ) : (
                    <span></span>
                  )}
                  <span className={`font-bold ${application.coverLetter.length < 50 ? 'text-gray-400' : 'text-green-600'}`}>
                    {application.coverLetter.length}/50
                  </span>
                </div>
              </div>
              <div className="flex gap-4">
                <button 
                  type="submit" 
                  className="flex-1 h-16 flex items-center justify-center gap-3 bg-[#2B4C6F] text-white font-bold text-xl rounded-xl border-2 border-black shadow-[4px_4px_0px_#000] hover:shadow-[5px_5px_0px_#000] active:translate-y-1 active:shadow-none transition-all disabled:opacity-50"
                  disabled={submitting}
                  data-testid="submit-application-btn"
                >
                  {submitting ? (
                    <>
                      <SpinnerGap size={28} weight="bold" className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <PaperPlaneTilt size={28} weight="bold" />
                      Send Application
                    </>
                  )}
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowApplyForm(false);
                    setErrors({});
                  }}
                  className="h-16 px-6 bg-gray-100 text-gray-700 font-bold text-lg rounded-xl border-2 border-gray-300 hover:bg-gray-200 transition-all"
                  disabled={submitting}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Job Info Card */}
        <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000] mb-6">
          <h2 className="text-xl font-black text-gray-900 mb-4">About This Job</h2>
          <p className="text-gray-700 text-lg leading-relaxed mb-6">
            {job.description}
          </p>
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4">
              <p className="text-sm font-bold text-gray-500 mb-1">Pay</p>
              <p className="text-xl font-black text-green-600">
                ${job.budget_min?.toLocaleString()} - ${job.budget_max?.toLocaleString()}
              </p>
            </div>
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4">
              <p className="text-sm font-bold text-gray-500 mb-1">Duration</p>
              <p className="text-xl font-black text-gray-900">{job.duration || 'Flexible'}</p>
            </div>
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4">
              <p className="text-sm font-bold text-gray-500 mb-1">Location</p>
              <p className="text-lg font-bold text-gray-900">{job.location}</p>
            </div>
            <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-4">
              <p className="text-sm font-bold text-gray-500 mb-1">Applicants</p>
              <p className="text-xl font-black text-gray-900">{job.applications?.length || 0}</p>
            </div>
          </div>
        </div>

        {/* Skills Required */}
        {job.required_skills?.length > 0 && (
          <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000] mb-6">
            <h2 className="text-xl font-black text-gray-900 mb-4">Skills Needed</h2>
            <div className="flex flex-wrap gap-3">
              {job.required_skills.map((skill, index) => (
                <span 
                  key={index} 
                  className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-800 font-bold rounded-full border-2 border-blue-300"
                >
                  <CheckCircle size={20} weight="fill" />
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Photo Documentation Section */}
        {(isOwner || isAssignedWorker) && (
          <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000] mb-6">
            <JobPhotoGallery 
              jobId={jobId}
              canUpload={isOwner || isAssignedWorker}
              showGrouped={true}
            />
          </div>
        )}

        {/* Time Tracking Section - Only for Assigned Workers */}
        {isAssignedWorker && (
          <div className="mb-6">
            <TimeClockWidget 
              jobId={jobId}
              jobTitle={job?.title}
              onStatusChange={(status) => console.log('Time status:', status)}
            />
          </div>
        )}

        {/* Cost Tracking Section - Only for Job Owners */}
        {isOwner && (
          <div className="mb-6">
            <JobCostTracker jobId={jobId} />
          </div>
        )}

        {/* Job Documents Section - For Owners and Assigned Workers */}
        {(isOwner || isAssignedWorker) && (
          <div className="mb-6">
            <JobDocuments 
              jobId={jobId}
              isContractor={isOwner}
              isAssignedWorker={isAssignedWorker}
            />
          </div>
        )}

        {/* Client Portal Access - Only for Job Owners */}
        {isOwner && (
          <div className="mb-6">
            <ClientInviteManager 
              jobId={jobId}
              jobTitle={job?.title}
            />
          </div>
        )}

        {/* Requirements */}
        <div className="bg-white border-2 border-black rounded-xl p-6 shadow-[4px_4px_0px_#000]">
          <h2 className="text-xl font-black text-gray-900 mb-4">What You Need</h2>
          <div className="space-y-4">
            {[
              { title: 'Valid License', desc: 'Required licenses for this work' },
              { title: 'Insurance', desc: 'Liability insurance required' },
              { title: 'Experience', desc: '3-5 years preferred' },
              { title: 'References', desc: 'From previous clients' }
            ].map((req, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <CheckCircle size={28} weight="fill" className="text-green-600 flex-shrink-0" />
                <div>
                  <p className="font-bold text-gray-900">{req.title}</p>
                  <p className="text-gray-600">{req.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Matching Workers Modal */}
      <MatchingWorkersModal
        jobId={jobId}
        jobTitle={job?.title}
        isOpen={showMatchingWorkers}
        onClose={() => setShowMatchingWorkers(false)}
      />

      {/* Plan limit upgrade prompt */}
      <UpgradePrompt
        open={!!upgradeInfo}
        onClose={() => setUpgradeInfo(null)}
        limitInfo={upgradeInfo}
      />

      {/* E-Signature Document Drafting */}
      <CreateDocumentModal
        open={showCreateDoc}
        onOpenChange={setShowCreateDoc}
        jobId={jobId}
        documentType={createDocType}
        defaultSigners={docPrefill.signers}
        parentDocumentId={docPrefill.parentDocumentId}
        onCreated={handleDocCreated}
      />
    </div>
  );
};

export default JobDetails;
